import { Button } from '@design-system';
import { CoverageSplit } from '../components/figures';
import { ScenarioSummary } from '../components/scenario-summary';
import { SelectedCoverageSummary } from '../components/selected-coverage-summary';
import { StepFrame } from '../components/step-frame';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { usePlanTotals } from '../hooks/use-plan-totals';
import { approxMoney } from '../lib/format';
import type { ConsumerStepProps } from './step-props';

export function ReviewStep({ clientId }: ConsumerStepProps) {
  const { goTo, back } = useConsumerFlow(clientId);
  const { scenario, selectedQuote, projectedPayout, outOfPocket, summaryStep } = usePlanTotals(clientId);
  if (!scenario || !selectedQuote) return null;
  const canWalkThrough = summaryStep === 'walkthrough-summary';

  return (
    <StepFrame
      step="review"
      title="Ready to take the next step?"
      subtitle={<ScenarioSummary scenario={scenario} variant="sentence" />}
      onBack={() => back(canWalkThrough ? summaryStep : 'coverage')}
      help={
        <>
          <p>You don’t have to solve every future cost today. Your application is the next step toward checking eligibility and final terms.</p>
          <div className="flex flex-col items-start">
            {canWalkThrough && (
              <Button variant="link" className="px-0 has-[>svg]:px-0" onClick={() => goTo('walkthrough')}>
                Walk through a care event
              </Button>
            )}
            <Button variant="link" className="px-0 has-[>svg]:px-0" onClick={() => goTo('medicaid', { returnTo: 'review' })}>
              What if I can’t cover the rest?
            </Button>
          </div>
        </>
      }
      primary={{ label: 'Prepare my application', onClick: () => goTo('application-start') }}
      secondary={{ label: 'I want to change something', onClick: () => goTo('change') }}
      next="confirm the details already in your intake."
    >
      <SelectedCoverageSummary quote={selectedQuote} />
      <CoverageSplit projectedPayout={projectedPayout} outOfPocket={outOfPocket} short />
      <p className="text-muted-foreground text-sm">
        Family: {scenario.familyHoursPerWeek.toFixed(1)} hours a week on average.{' '}
        {scenario.familySavings > 0
          ? `${approxMoney(scenario.familySavings)} in avoided paid care is already reflected above.`
          : 'No paid-care saving assumed.'}
      </p>
    </StepFrame>
  );
}
